import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useProfile } from '../hooks/useProfile'
import { FirebaseStorageService } from '../services/FirebaseStorageService'
import { FirestoreService, RecipeRating } from '../services/FirestoreService'
import { GeneratedRecipe, UserPreferences } from '../lib/claudeRecipeGeneration'
import { SavedRecipesService } from '../lib/savedRecipesService'
import { ClaudeVisionService } from '../lib/claudeVision'
import { UsageTrackingService, UsageLimits } from '../lib/usageTracking'
import { fetchWithRetry, getAuthHeaders } from '../lib/apiUtils'
import LoadingSpinner from './LoadingSpinner'
import StarRating from './StarRating'
import RatingModal from './RatingModal'
import './RecipeDiscovery.css'
import './IngredientConfirmation.css'
import './UsageSummary.css'

interface RecipeWithMissing extends GeneratedRecipe {
  missing_ingredients: string[]
  missing_count: number
  is_saved?: boolean
}

// Recipe cache helpers (48 hour TTL) with HMAC integrity verification
const CACHE_TTL_MS = 48 * 60 * 60 * 1000; // 48 hours
const CACHE_PREFIX = 'recipe_cache_';

interface CachedRecipes {
  recipes: RecipeWithMissing[];
  timestamp: number;
  cacheKey: string;
  signature?: string;
}

// Simple hash-based signature for cache integrity
// Prevents casual tampering; not cryptographic security (no secrets in browser)
function computeSignature(data: string, userId: string): string {
  let hash = 0;
  const str = userId + ':' + data;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash + char) | 0;
  }
  // Mix in a second pass for better distribution
  for (let i = str.length - 1; i >= 0; i--) {
    const char = str.charCodeAt(i);
    hash = ((hash << 7) - hash + char) | 0;
  }
  return Math.abs(hash).toString(36);
}

function getCachedRecipes(cacheKey: string, userId?: string): RecipeWithMissing[] | null {
  try {
    const cached = localStorage.getItem(CACHE_PREFIX + cacheKey);
    if (!cached) return null;

    const data: CachedRecipes = JSON.parse(cached);
    const isExpired = Date.now() - data.timestamp > CACHE_TTL_MS;

    if (isExpired) {
      localStorage.removeItem(CACHE_PREFIX + cacheKey);
      return null;
    }

    // Verify integrity signature
    if (userId && data.signature) {
      const payload = JSON.stringify({ recipes: data.recipes, timestamp: data.timestamp, cacheKey: data.cacheKey });
      const expected = computeSignature(payload, userId);
      if (data.signature !== expected) {
        localStorage.removeItem(CACHE_PREFIX + cacheKey);
        return null;
      }
    }

    return data.recipes;
  } catch {
    return null;
  }
}

function setCachedRecipes(cacheKey: string, recipes: RecipeWithMissing[], userId?: string): void {
  try {
    const timestamp = Date.now();
    const payload = JSON.stringify({ recipes, timestamp, cacheKey });
    const signature = userId ? computeSignature(payload, userId) : undefined;
    const data: CachedRecipes = {
      recipes,
      timestamp,
      cacheKey,
      signature
    };
    localStorage.setItem(CACHE_PREFIX + cacheKey, JSON.stringify(data));

    // Clean up old cache entries (keep max 10)
    cleanupOldCacheEntries();
  } catch {
    // localStorage might be full, ignore
  }
}

function cleanupOldCacheEntries(): void {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX));
    if (keys.length > 10) {
      // Sort by timestamp and remove oldest
      const entries = keys.map(k => {
        try {
          const data = JSON.parse(localStorage.getItem(k) || '{}');
          return { key: k, timestamp: data.timestamp || 0 };
        } catch {
          return { key: k, timestamp: 0 };
        }
      }).sort((a, b) => a.timestamp - b.timestamp);

      // Remove oldest entries
      entries.slice(0, entries.length - 10).forEach(e => localStorage.removeItem(e.key));
    }
  } catch {
    // Ignore cleanup errors
  }
}

function clearRecipeCache(): void {
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith(CACHE_PREFIX))
      .forEach(k => localStorage.removeItem(k));
  } catch {
    // Ignore
  }
}

// Generate cache key client-side (simple hash for browser)
function generateClientCacheKey(ingredients: string[], preferences: UserPreferences | undefined): string {
  const sortedIngredients = [...ingredients].sort().join(',');
  const dietaryKey = preferences?.dietary_preferences || 'none';
  const cuisineKey = (preferences?.food_genres || []).sort().join(',') || 'any';
  const combined = `${sortedIngredients}|${dietaryKey}|${cuisineKey}`;
  // Simple deterministic hash
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

const RecipeDiscovery: React.FC = () => {
  const { user } = useAuth()
  const { profile } = useProfile()
  const [userIngredients, setUserIngredients] = useState<string[]>([])
  const [suggestedRecipes, setSuggestedRecipes] = useState<RecipeWithMissing[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingRecipe, setSavingRecipe] = useState<string | null>(null)
  const [selectedRecipe, setSelectedRecipe] = useState<RecipeWithMissing | null>(null)
  const [showFullRecipe, setShowFullRecipe] = useState(false)
  const [isEditingIngredients, setIsEditingIngredients] = useState(false)
  const [editableIngredients, setEditableIngredients] = useState<string>('')
  const [, setConfirmedIngredients] = useState<string[]>([])
  const [possibleIngredients, setPossibleIngredients] = useState<string[]>([])
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [usageLimits, setUsageLimits] = useState<UsageLimits | null>(null)
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false)
  const [showGenerateButton, setShowGenerateButton] = useState(false)

  // Rating state
  const [showRatingModal, setShowRatingModal] = useState(false)
  const [recipeToRate, setRecipeToRate] = useState<RecipeWithMissing | null>(null)
  const [userRatings, setUserRatings] = useState<Map<string, RecipeRating>>(new Map())

  const analyzeIngredients = async (imageUrls: string[]): Promise<string[]> => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Starting ingredient analysis with image URLs:', imageUrls)
      }

      // Use Claude Vision service directly with new improved prompts and user preferences
      const ingredients = await ClaudeVisionService.analyzeIngredients(imageUrls, 0.8, profile, user?.id)

      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Successfully analyzed ${imageUrls.length} images, found ${ingredients.length} ingredients:`, ingredients)
      }

      if (ingredients.length > 0) {
        // Filter out obvious staples that don't need confirmation
        const assumedStaples = ['salt', 'pepper', 'olive oil', 'water', 'vegetable oil', 'cooking oil']
        const filteredIngredients = ingredients.filter(
          ing => !assumedStaples.includes(ing.toLowerCase())
        )

        // The backend returns ingredients sorted by confidence (high first, medium second)
        // High confidence items are roughly the first 70%
        const splitPoint = Math.ceil(filteredIngredients.length * 0.7)
        const highConfidence = filteredIngredients.slice(0, splitPoint)
        const mediumConfidence = filteredIngredients.slice(splitPoint)

        setConfirmedIngredients(highConfidence)
        setPossibleIngredients(mediumConfidence)
        setShowConfirmation(mediumConfidence.length > 0)

        return filteredIngredients
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ No ingredients detected in images. This might be normal if images don\'t contain visible food items.')
      }
      return []

    } catch (error) {
      console.error('❌ Ingredient analysis failed:', error)
      setError('Vision analysis failed. Using basic ingredients. Please check your images are uploaded correctly.')

      // Fallback to mock data if API fails
      return [
        'eggs', 'milk', 'bread', 'tomatoes', 'onions', 'cheese',
        'tofu', 'rice', 'olive oil', 'garlic', 'beans', 'lentils'
      ]
    }
  }

  const convertRecipeToWithMissing = (
    recipe: GeneratedRecipe,
    availableIngredients: string[]
  ): RecipeWithMissing => {
    const missing = recipe.ingredients.filter(
      ingredient => !availableIngredients.some(
        available => available.toLowerCase().includes(ingredient.toLowerCase()) ||
                    ingredient.toLowerCase().includes(available.toLowerCase())
      )
    )

    return {
      ...recipe,
      missing_ingredients: missing,
      missing_count: missing.length,
      is_saved: false // Will be updated when we check saved recipes
    }
  }

  const saveRecipe = async (recipe: GeneratedRecipe) => {
    if (!user) return

    setSavingRecipe(recipe.id)
    try {
      const saved = await SavedRecipesService.saveRecipe(user.id, recipe)
      if (saved) {
        // Update the recipe to show it's saved
        setSuggestedRecipes(prev =>
          prev.map(r => r.id === recipe.id ? { ...r, is_saved: true } : r)
        )
      }
    } catch (error) {
      console.error('Failed to save recipe:', error)
    } finally {
      setSavingRecipe(null)
    }
  }

  const openRatingModal = (recipe: RecipeWithMissing) => {
    setRecipeToRate(recipe)
    setShowRatingModal(true)
  }

  const handleRatingSubmit = async (ratingData: Omit<RecipeRating, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return

    const ratingWithUser = { ...ratingData, user_id: user.id }
    const savedRating = await FirestoreService.rateRecipe(ratingWithUser)

    // Update local ratings cache
    setUserRatings(prev => new Map(prev).set(savedRating.recipe_id, savedRating))
  }

  const viewFullRecipe = (recipe: RecipeWithMissing) => {
    setSelectedRecipe(recipe)
    setShowFullRecipe(true)
  }

  const closeFullRecipe = useCallback(() => {
    setShowFullRecipe(false)
    setSelectedRecipe(null)
  }, [])

  // Handle escape key for modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showRatingModal) {
        setShowRatingModal(false)
        setRecipeToRate(null)
      }
      if (event.key === 'Escape' && showFullRecipe) {
        closeFullRecipe()
      }
      if (event.key === 'Escape' && showUpgradePrompt) {
        setShowUpgradePrompt(false)
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [showFullRecipe, showUpgradePrompt, showRatingModal, closeFullRecipe])

  const startEditingIngredients = () => {
    setEditableIngredients(userIngredients.join(', '))
    setIsEditingIngredients(true)
  }

  const saveEditedIngredients = async () => {
    const newIngredients = editableIngredients
      .split(',')
      .map(item => item.trim().toLowerCase())
      .filter(item => item.length > 0)
      .filter((item, index, arr) => arr.indexOf(item) === index) // Remove duplicates

    setUserIngredients(newIngredients)
    setIsEditingIngredients(false)

    // Regenerate recipes with updated ingredients
    setLoading(true)
    try {
      await generateRecipes(newIngredients)
    } catch (error) {
      console.error('Failed to regenerate recipes:', error)
      setError('Failed to generate recipes with updated ingredients')
    } finally {
      setLoading(false)
    }
  }

  const cancelEditingIngredients = () => {
    setIsEditingIngredients(false)
    setEditableIngredients('')
  }

  const confirmIngredient = (ingredient: string) => {
    // Use functional state updates to avoid race conditions
    setConfirmedIngredients(prev => [...prev, ingredient])
    setPossibleIngredients(prev => prev.filter(item => item !== ingredient))

    // Update main ingredients list with functional update
    setUserIngredients(prev => [...prev, ingredient])
  }

  const rejectIngredient = (ingredient: string) => {
    setPossibleIngredients(prev => prev.filter(item => item !== ingredient))
  }

  const finishConfirmation = async () => {
    setShowConfirmation(false)

    // Use a callback to get the current state value to avoid stale closures
    // We need to check the actual current state
    setUserIngredients(currentIngredients => {
      // Trigger recipe generation asynchronously after state is confirmed
      if (currentIngredients.length > 0) {
        // Use setTimeout to avoid calling generateRecipes during state update
        setTimeout(() => generateRecipes(currentIngredients), 0)
      }
      return currentIngredients
    })
  }

  const clearCacheAndReanalyze = async () => {
    if (!user) {
      setError('User not authenticated')
      return
    }

    // Clear Firebase ingredients cache
    if (user) {
      await FirestoreService.clearUserIngredients(user.id)
    }

    // Clear local recipe cache
    clearRecipeCache()

    // Reset component state
    setUserIngredients([])
    setSuggestedRecipes([])
    setError(null)
    setLoading(true)

    try {
      // Get fresh images and reanalyze
      const imageUrls = await FirebaseStorageService.getUserImages(user.id)

      if (imageUrls.length > 0) {
        const ingredients = await analyzeIngredients(imageUrls)
        setUserIngredients(ingredients)
        await generateRecipes(ingredients, true) // Skip cache on fresh analysis
      } else {
        setError('Please upload some ingredient photos first')
      }
    } catch (error) {
      console.error('Failed to reanalyze ingredients:', error)
      setError('Failed to analyze ingredients. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const getUserPreferences = (): UserPreferences => {
    return {
      dietary_preferences: profile?.dietary_preferences || undefined,
      food_genres: profile?.food_genres || undefined,
      cooking_skill: 'intermediate', // Could be added to profile later
      time_preference: 'normal' // Could be added to profile later
    }
  }

  const getAdaptiveThreshold = (pantrySize: number) => {
    if (pantrySize <= 3) return Math.min(3, pantrySize + 2) // Very small pantry: allow up to 3-5 missing
    if (pantrySize <= 6) return 2 // Small pantry: stick to 2 missing max
    return 2 // Well-stocked pantry: stick to 2 missing max
  }

  const generateRecipes = async (ingredients: string[], skipCache = false) => {
    try {
      const preferences = getUserPreferences()

      if (process.env.NODE_ENV === 'development') {
        console.log('🍳 Generating recipes for ingredients:', ingredients)
      }

      // If no ingredients, load saved recipes as fallback
      if (ingredients.length === 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('📚 No ingredients detected, loading saved recipes instead')
        }
        await loadSavedRecipes([])
        return
      }

      // Check local cache first (unless explicitly skipping)
      if (!skipCache) {
        const clientCacheKey = generateClientCacheKey(ingredients, preferences)
        const cachedRecipes = getCachedRecipes(clientCacheKey, user?.id)
        if (cachedRecipes && cachedRecipes.length > 0) {
          setSuggestedRecipes(cachedRecipes)
          return
        }
      }

      // Check usage limits before generating
      if (user) {
        const usageCheck = await UsageTrackingService.canGenerateRecipes(user.id, profile)
        setUsageLimits(usageCheck.limits)

        if (!usageCheck.canGenerate) {
          setError(usageCheck.reason || 'Usage limit exceeded')
          setShowUpgradePrompt(true)
          return
        }

        // Track this generation attempt
        await UsageTrackingService.trackRecipeGeneration(user.id, usageCheck.limits.recipesPerGeneration)
      }

      // Use tier-appropriate recipe count
      const recipeCount = usageLimits?.recipesPerGeneration || 3
      const tier = profile?.subscription_tier || 'basic'

      // Call our backend for recipe generation (with retry)
      const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001'
      const authHeaders = await getAuthHeaders()
      const response = await fetchWithRetry(
        `${baseUrl}/api/generate-recipes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({ ingredients, preferences, count: recipeCount, tier })
        },
        { maxRetries: 2 } // Fewer retries for user-facing requests
      )

      if (!response.ok) {
        throw new Error(`Backend API error: ${response.status}`)
      }

      const data = await response.json()
      const recipes = data.recipes || []

      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Generated ${recipes.length} recipes`)
      }

      const maxMissing = getAdaptiveThreshold(ingredients.length)

      // Convert to RecipeWithMissing format and filter
      const recipesWithMissing = recipes
        .map((recipe: GeneratedRecipe) => convertRecipeToWithMissing(recipe, ingredients))
        .filter((recipe: RecipeWithMissing) => recipe.missing_count <= maxMissing)
        .sort((a: RecipeWithMissing, b: RecipeWithMissing) => a.missing_count - b.missing_count)

      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Recipes after processing:', recipesWithMissing.length)
      }

      // Cache the results for future use (use client key for consistency)
      const clientCacheKey = generateClientCacheKey(ingredients, preferences)
      if (recipesWithMissing.length > 0) {
        setCachedRecipes(clientCacheKey, recipesWithMissing, user?.id)
      }

      setSuggestedRecipes(recipesWithMissing)

      if (recipesWithMissing.length === 0) {
        await loadSavedRecipes(ingredients)
      }

    } catch (error) {
      console.error('Recipe generation failed:', error)
      await loadSavedRecipes(ingredients)
      setError('Using your saved recipes. Recipe generation temporarily unavailable.')
    }
  }

  const loadSavedRecipes = async (ingredients: string[]) => {
    if (!user) return

    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Loading saved recipes for user:', user.id)
    }

    try {
      const maxMissing = getAdaptiveThreshold(ingredients.length)
      const saved = await SavedRecipesService.getRecipesByIngredients(user.id, ingredients, maxMissing)

      const recipesWithMissing = saved.map(savedRecipe => {
        const recipe = SavedRecipesService.savedRecipeToGenerated(savedRecipe)
        return { ...convertRecipeToWithMissing(recipe, ingredients), is_saved: true }
      })

      setSuggestedRecipes(recipesWithMissing)

      if (recipesWithMissing.length === 0) {
        setError('No recipes found. Try uploading more ingredient photos or generate some new recipes!')
      }

    } catch (error) {
      console.error('Failed to load saved recipes:', error)
      setError('Failed to load recipes. Please try again.')
    }
  }

  const handleGenerateRecipes = async () => {
    setShowGenerateButton(false)
    await generateRecipes(userIngredients)
  }

  // Save ingredients to Firebase when they change
  useEffect(() => {
    const saveIngredients = async () => {
      if (user && userIngredients.length > 0) {
        try {
          await FirestoreService.saveUserIngredients(user.id, userIngredients)
        } catch (error) {
          console.error('Failed to save ingredients to Firebase:', error)
        }
      }
    }
    saveIngredients()
  }, [userIngredients, user])

  // Load user data when user changes
  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        try {
          // Load ingredients from Firebase
          const savedIngredients = await FirestoreService.getUserIngredients(user.id)
          setUserIngredients(savedIngredients)
        } catch (error) {
          console.error('Failed to load ingredients from Firebase:', error)
          setUserIngredients([])
        }
      } else {
        // Clear data when no user
        setUserIngredients([])
        setSuggestedRecipes([])
      }
    }
    loadUserData()
  }, [user])

  // Load usage limits when component mounts
  useEffect(() => {
    const loadUsageLimits = async () => {
      if (user && profile) {
        const limits = await UsageTrackingService.getUserUsageLimits(user.id, profile)
        setUsageLimits(limits)
      }
    }
    loadUsageLimits()
  }, [user, profile])

  // Load user ratings when component mounts
  useEffect(() => {
    const loadUserRatings = async () => {
      if (user) {
        try {
          const ratings = await FirestoreService.getUserRatings(user.id)
          const ratingsMap = new Map(ratings.map(r => [r.recipe_id, r]))
          setUserRatings(ratingsMap)
        } catch (error) {
          console.error('Failed to load user ratings:', error)
        }
      }
    }
    loadUserRatings()
  }, [user])

  useEffect(() => {
    const loadUserIngredientsAndRecipes = async () => {
      if (!user) {
        setLoading(false)
        return
      }

      // Wait a bit for ingredients to load from Firebase
      await new Promise(resolve => setTimeout(resolve, 500))

      // Safety timeout to ensure loading never gets stuck
      const timeoutId = setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⏰ Loading timeout reached, forcing completion')
        }
        setLoading(false)
      }, 30000) // 30 second timeout

      try {
        // Get user's uploaded images
        const imageUrls = await FirebaseStorageService.getUserImages(user.id)

        if (imageUrls.length > 0) {
          // Check if we need to analyze ingredients (no ingredients saved in Firebase)
          const savedIngredients = await FirestoreService.getUserIngredients(user.id)

          if (savedIngredients.length === 0) {
            // No saved ingredients, analyze images
            const ingredients = await analyzeIngredients(imageUrls)
            setUserIngredients(ingredients)

            if (ingredients.length > 0) {
              setShowGenerateButton(true)
            } else {
              if (process.env.NODE_ENV === 'development') {
                console.log('🍳 No ingredients detected, will show saved recipes instead')
              }
              await loadSavedRecipes([])
            }
          } else {
            // Use saved ingredients - show generate button if no recipes exist
            if (suggestedRecipes.length === 0) {
              setShowGenerateButton(true)
            }
          }
        } else {
          // No images uploaded
          const savedIngredients = await FirestoreService.getUserIngredients(user.id)
          if (savedIngredients.length === 0) {
            setError('Please upload some ingredient photos first')
          }
        }
      } catch (err) {
        console.error('Error loading recipes:', err)
        setError('Failed to load recipe suggestions. Using saved recipes.')
        await loadSavedRecipes(userIngredients)
      } finally {
        clearTimeout(timeoutId)
        setLoading(false)
      }
    }

    loadUserIngredientsAndRecipes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]) // Only depend on user to prevent infinite loops

  if (loading) {
    return <LoadingSpinner message="Analyzing your ingredients and generating recipes..." />
  }

  if (error && userIngredients.length === 0) {
    return (
      <div className="recipe-discovery error" role="alert">
        <p>{error}</p>
        <div className="error-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            className="clear-cache-btn"
            onClick={clearCacheAndReanalyze}
            style={{ padding: '10px 20px', cursor: 'pointer' }}
            aria-label="Clear cache and perform fresh ingredient analysis"
          >
            🔄 Fresh Analysis
          </button>
          <button
            className="edit-ingredients-btn"
            onClick={startEditingIngredients}
            style={{ padding: '10px 20px', cursor: 'pointer' }}
            aria-label="Manually add ingredients"
          >
            ✏️ Add Ingredients Manually
          </button>
        </div>
      </div>
    )
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('🎨 RecipeDiscovery rendering with:', {
      loading,
      error,
      suggestedRecipesCount: suggestedRecipes.length,
      userIngredientsCount: userIngredients.length
    })
  }

  return (
    <div className="recipe-discovery">
      <div className="recipe-header">
        <h2>Recipe Discovery</h2>
        <p className="recipe-subtitle">Based on your uploaded ingredients</p>
      </div>

      <div className="ingredients-summary">
        <div className="ingredients-header">
          <h3>Your Available Ingredients</h3>
          {!isEditingIngredients && (
            <div className="ingredient-buttons">
              {(showGenerateButton || (userIngredients.length > 0 && suggestedRecipes.length === 0)) && (
                <button
                  className="generate-recipes-btn"
                  onClick={handleGenerateRecipes}
                  title="Generate recipes with your current ingredients"
                >
                  🍳 Generate Recipes
                </button>
              )}
              <button
                className="edit-ingredients-btn"
                onClick={startEditingIngredients}
                title="Edit or add ingredients manually"
                aria-label="Edit ingredients manually"
              >
                ✏️ Edit
              </button>
              <button
                className="clear-cache-btn"
                onClick={clearCacheAndReanalyze}
                title="Clear cache and reanalyze fresh photos"
                aria-label="Clear cache and perform fresh ingredient analysis"
              >
                🔄 Fresh Analysis
              </button>
            </div>
          )}
        </div>

        {isEditingIngredients ? (
          <div className="ingredient-editor">
            <textarea
              value={editableIngredients}
              onChange={(e) => setEditableIngredients(e.target.value)}
              placeholder="Add ingredients separated by commas (e.g., tomatoes, eggs, cheese, onions)"
              className="ingredient-textarea"
              rows={4}
              aria-label="Edit ingredients list"
            />
            <div className="editor-buttons">
              <button 
                className="save-btn" 
                onClick={saveEditedIngredients}
                aria-label="Save ingredients and generate recipes"
              >
                ✅ Save & Generate Recipes
              </button>
              <button 
                className="cancel-btn" 
                onClick={cancelEditingIngredients}
                aria-label="Cancel editing and discard changes"
              >
                ❌ Cancel
              </button>
            </div>
            <p className="editor-help">
              💡 Tip: Add ingredients that Claude missed or remove incorrect ones
            </p>
          </div>
        ) : showConfirmation ? (
          <div className="ingredient-confirmation" role="region" aria-label="Ingredient confirmation">
            <h4>🤔 Confirm These Ingredients</h4>
            <p>We detected some additional items. Please confirm which ones you actually have:</p>

            <div className="possible-ingredients" role="list">
              {possibleIngredients.map((ingredient, index) => (
                <div key={index} className="ingredient-confirmation-item" role="listitem">
                  <span className="ingredient-name">{ingredient}</span>
                  <div className="confirmation-buttons">
                    <button
                      className="confirm-btn"
                      onClick={() => confirmIngredient(ingredient)}
                      title="I have this"
                      aria-label={`Confirm you have ${ingredient}`}
                    >
                      ✅
                    </button>
                    <button
                      className="reject-btn"
                      onClick={() => rejectIngredient(ingredient)}
                      title="I don't have this"
                      aria-label={`Reject ${ingredient}`}
                    >
                      ❌
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {possibleIngredients.length === 0 && (
              <div className="confirmation-complete" role="status" aria-live="polite">
                <p>✅ All ingredients confirmed!</p>
                <button
                  className="finish-confirmation-btn"
                  onClick={finishConfirmation}
                  aria-label="Finish confirmation and generate recipes"
                >
                  Generate Recipes
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="ingredient-tags">
            {userIngredients.length > 0 ? (
              <>
                <h4>✅ Confirmed Ingredients ({userIngredients.length})</h4>
                <div role="list" aria-label="Your confirmed ingredients">
                  {userIngredients.map((ingredient, index) => (
                    <span key={index} className="ingredient-tag confirmed" role="listitem">
                      {ingredient}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <p className="no-ingredients">
                No ingredients detected yet. Upload photos or manually add ingredients above.
              </p>
            )}
          </div>
        )}
      </div>

      {(profile?.dietary_preferences || (profile?.food_genres && profile?.food_genres.length > 0)) && (
        <div className="preferences-summary">
          <h3>Your Preferences Applied ✨</h3>
          <div className="preference-tags">
            {profile?.dietary_preferences && (
              <div className="preference-group">
                <span className="preference-label">Dietary:</span>
                {profile.dietary_preferences.split(',').map((pref, index) => (
                  <span key={index} className="preference-tag dietary">
                    {pref.trim()}
                  </span>
                ))}
              </div>
            )}
            {profile?.food_genres && profile.food_genres.length > 0 && (
              <div className="preference-group">
                <span className="preference-label">Cuisines:</span>
                {profile.food_genres.map((genre, index) => (
                  <span key={index} className="preference-tag cuisine">
                    {genre}
                  </span>
                ))}
              </div>
            )}
          </div>
          <p className="preferences-note">Recipes are generated to match your preferences</p>
        </div>
      )}

      {usageLimits && usageLimits.isBasicTier && (
        <div className="usage-summary">
          <div className="tier-info">
            <h4>📊 Basic Plan Usage</h4>
            <div className="usage-stats">
              <span className="stat">
                <strong>{usageLimits.recipesPerGeneration}</strong> recipes per generation
              </span>
              <span className="stat">
                <strong>{usageLimits.remainingGenerations}</strong> of {usageLimits.maxGenerationsPerWeek} generations left this week
              </span>
            </div>
            {usageLimits.remainingGenerations <= 1 && (
              <div className="upgrade-prompt">
                <p>⚡ Almost at your weekly limit! </p>
                <button className="upgrade-btn" onClick={() => setShowUpgradePrompt(true)}>
                  Upgrade to Premium for Unlimited Recipes
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showUpgradePrompt && (
        <div
          className="upgrade-modal-overlay"
          onClick={() => setShowUpgradePrompt(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="upgrade-modal-title"
        >
          <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="upgrade-modal-title">🚀 Upgrade to Premium</h3>
            <div className="feature-comparison">
              <div className="plan basic-plan">
                <h4>Basic Plan</h4>
                <ul>
                  <li>3 recipes per generation</li>
                  <li>5 generations per week</li>
                  <li>Basic ingredient detection</li>
                </ul>
              </div>
              <div className="plan premium-plan">
                <h4>Premium Plan</h4>
                <ul>
                  <li>10 recipes per generation</li>
                  <li>Unlimited generations</li>
                  <li>Advanced ingredient detection</li>
                  <li>Priority support</li>
                  <li>Export recipes to PDF</li>
                </ul>
              </div>
            </div>
            <div className="modal-buttons">
              <button className="upgrade-now-btn">
                Upgrade Now - $9.99/month
              </button>
              <button className="cancel-btn" onClick={() => setShowUpgradePrompt(false)}>
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="recipe-suggestions">
        <h3>AI-Generated Recipes {userIngredients.length <= 3 ? '(minimal shopping needed)' : '(0-2 ingredients needed)'}</h3>
        {userIngredients.length <= 3 && (
          <p className="adaptive-notice">
            Since you have a smaller pantry, we're showing recipes that may need 3-5 ingredients to give you more options.
          </p>
        )}
        {suggestedRecipes.length === 0 ? (
          <p>No recipes found with your current ingredients. Try uploading more photos!</p>
        ) : (
          <div className="recipe-grid">
            {suggestedRecipes.map(recipe => (
              <div key={recipe.id} className="recipe-card">
                <div className="recipe-header">
                  <h4>{recipe.title}</h4>
                  <div className="recipe-meta">
                    <span className="cuisine">{Array.isArray(recipe.cuisine) ? recipe.cuisine.join(', ') : recipe.cuisine}</span>
                    <span className="time">{recipe.prep_time + recipe.cook_time} min</span>
                    <span className="difficulty">{recipe.difficulty}</span>
                  </div>
                </div>

                <p className="recipe-description">{recipe.description}</p>

                <div className="missing-ingredients">
                  {recipe.missing_count === 0 ? (
                    <div className="perfect-match">✅ Perfect match! No shopping needed</div>
                  ) : (
                    <div className="need-to-buy">
                      <span className="missing-count">Need {recipe.missing_count} ingredient{recipe.missing_count > 1 ? 's' : ''}:</span>
                      <div className="missing-list">
                        {recipe.missing_ingredients.map((ingredient, index) => (
                          <span key={index} className="missing-ingredient">
                            {ingredient}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {recipe.tips && recipe.tips.length > 0 && (
                  <div className="recipe-tips">
                    <h5>💡 Tips:</h5>
                    <ul>
                      {recipe.tips.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {userRatings.has(recipe.id) && (
                  <div className="recipe-card-rating">
                    <StarRating
                      rating={userRatings.get(recipe.id)!.rating}
                      readonly
                      size="small"
                    />
                    {userRatings.get(recipe.id)!.would_make_again && (
                      <span className="would-make-again">Would make again</span>
                    )}
                  </div>
                )}

                <div className="recipe-actions">
                  <button
                    className="view-recipe-btn"
                    onClick={() => viewFullRecipe(recipe)}
                  >
                    View Full Recipe
                  </button>
                  {!recipe.is_saved ? (
                    <button
                      className="save-recipe-btn"
                      onClick={() => saveRecipe(recipe)}
                      disabled={savingRecipe === recipe.id}
                    >
                      {savingRecipe === recipe.id ? 'Saving...' : '❤️ Save'}
                    </button>
                  ) : (
                    <span className="saved-indicator">✅ Saved</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full Recipe Modal */}
      {showFullRecipe && selectedRecipe && (
        <div
          className="recipe-modal-overlay"
          onClick={closeFullRecipe}
          role="dialog"
          aria-modal="true"
          aria-labelledby="recipe-modal-title"
        >
          <div className="recipe-modal" onClick={(e) => e.stopPropagation()}>
            <div className="recipe-modal-header">
              <h2 id="recipe-modal-title">{selectedRecipe.title}</h2>
              <button
                className="close-modal-btn"
                onClick={closeFullRecipe}
                aria-label="Close recipe details"
              >
                ✕
              </button>
            </div>

            <div className="recipe-modal-content">
              <div className="recipe-details">
                <div className="recipe-meta">
                  <span>🕐 Prep: {selectedRecipe.prep_time}min</span>
                  <span>🔥 Cook: {selectedRecipe.cook_time}min</span>
                  <span>👥 Serves: {selectedRecipe.servings}</span>
                  <span>📊 {selectedRecipe.difficulty}</span>
                </div>

                <div className="recipe-cuisine-tags">
                  <span className="cuisine-tag">
                    {Array.isArray(selectedRecipe.cuisine)
                      ? selectedRecipe.cuisine.join(', ')
                      : selectedRecipe.cuisine}
                  </span>
                  {selectedRecipe.dietary_tags && selectedRecipe.dietary_tags.length > 0 && (
                    selectedRecipe.dietary_tags.map((tag, index) => (
                      <span key={index} className="dietary-tag">{tag}</span>
                    ))
                  )}
                </div>

                <p className="recipe-description">{selectedRecipe.description}</p>
              </div>

              <div className="recipe-sections">
                <div className="ingredients-section">
                  <h3>🥘 Ingredients</h3>
                  <ul className="ingredients-list">
                    {selectedRecipe.ingredients.map((ingredient, index) => (
                      <li key={index}>{ingredient}</li>
                    ))}
                  </ul>
                </div>

                <div className="instructions-section">
                  <h3>📋 Instructions</h3>
                  <ol className="instructions-list">
                    {selectedRecipe.instructions.map((instruction, index) => (
                      <li key={index}>{instruction}</li>
                    ))}
                  </ol>
                </div>

                {selectedRecipe.tips && selectedRecipe.tips.length > 0 && (
                  <div className="tips-section">
                    <h3>💡 Tips</h3>
                    <ul className="tips-list">
                      {selectedRecipe.tips.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="recipe-modal-actions">
                {!selectedRecipe.is_saved ? (
                  <button
                    className="save-recipe-btn"
                    onClick={() => saveRecipe(selectedRecipe)}
                    disabled={savingRecipe === selectedRecipe.id}
                  >
                    {savingRecipe === selectedRecipe.id ? 'Saving...' : '❤️ Save Recipe'}
                  </button>
                ) : (
                  <span className="saved-indicator">✅ Recipe Saved</span>
                )}
                <button
                  className="rate-recipe-btn"
                  onClick={() => openRatingModal(selectedRecipe)}
                >
                  {userRatings.has(selectedRecipe.id) ? '⭐ Update Rating' : '⭐ Rate Recipe'}
                </button>
                <button
                  className="print-recipe-btn"
                  onClick={() => window.print()}
                  aria-label="Print this recipe"
                >
                  🖨️ Print
                </button>
                {userRatings.has(selectedRecipe.id) && (
                  <div className="user-rating-display">
                    <StarRating
                      rating={userRatings.get(selectedRecipe.id)!.rating}
                      readonly
                      size="small"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {recipeToRate && (
        <RatingModal
          isOpen={showRatingModal}
          onClose={() => {
            setShowRatingModal(false)
            setRecipeToRate(null)
          }}
          recipeId={recipeToRate.id}
          recipeTitle={recipeToRate.title}
          existingRating={userRatings.get(recipeToRate.id) || null}
          onSubmit={handleRatingSubmit}
        />
      )}
    </div>
  )
}

export default RecipeDiscovery